import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { handleRedirect } from "../auth/handleRedirect";
import { useAuth } from "./AuthContext";

// Rotas "neutras" onde é seguro redirecionar após login
const NEUTRAL_PATHS = ["/", "/login", "/auth", "/auth-options", "/perfil-novo"];

const PROTECTED_PREFIXES = ["/dashboard-", "/cadastro-", "/checkout", "/pagamento", "/meus-pagamentos"];
const isProtectedPath = (pathname) =>
  PROTECTED_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * Componente sem UI que escuta mudanças de autenticação globalmente.
 * Responsável por redirecionar após OAuth (Google, etc.) e garantir
 * que novos usuários OAuth sejam encaminhados para /perfil-novo.
 */
export function AuthListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserType } = useAuth();
  const redirectInFlightRef = useRef(false);
  const lastHandledSessionRef = useRef(null);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT") {
        redirectInFlightRef.current = false;
        lastHandledSessionRef.current = null;
        localStorage.removeItem("user_type");
        if (isProtectedPath(location.pathname)) {
          navigate("/login", { replace: true });
        }
        return;
      }

      if (event !== "SIGNED_IN" || !session) {
        return;
      }

      if (!NEUTRAL_PATHS.includes(location.pathname)) {
        return;
      }

      const sessionKey = session.access_token || session.user?.id;
      if (
        redirectInFlightRef.current ||
        (sessionKey && lastHandledSessionRef.current === sessionKey)
      ) {
        return;
      }

      redirectInFlightRef.current = true;
      try {
        await handleRedirect(session.user, navigate, setUserType);
        if (sessionKey) {
          lastHandledSessionRef.current = sessionKey;
        }
      } finally {
        redirectInFlightRef.current = false;
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  return null;
}
